import { sizeAt, type Note } from '../core/chart';
import type { Beat, Seconds } from '../core/types';
import { unitOf, type Point, type Rect, type Viewport } from '../render/view';

export const IDLE_RADIUS = 0.04;
export const FOLLOW_SECONDS: Seconds = 0.12;
export const SHRINK_SECONDS: Seconds = 0.18;
export const REGROW_SECONDS: Seconds = 0.1;

export interface SizeEase {
  readonly from: number;
  readonly at: Seconds;
  readonly span: Seconds;
}
export const HOME: Point = { x: 0, y: 0 };
export const TOUCH_LIFT_PX: Point = { x: -40, y: -100 };
const NO_LIFT: Point = { x: 0, y: 0 };

export function liftOf(pointerType: string): Point {
  return pointerType === 'touch' ? TOUCH_LIFT_PX : NO_LIFT;
}

export type Pointing = 'tip' | 'finger';

export interface Aim {
  readonly place: Point;
  placeAt(now: Seconds): Point;
  down(id: number, pointing: Pointing, at: Point, bounds: Point, now: Seconds): void;
  move(id: number, pointing: Pointing, at: Point, bounds: Point): void;
  up(id: number, now: Seconds): void;
  reset(): void;
}

interface Glide {
  readonly id: number;
  readonly from: Point;
  readonly at: Seconds;
}

export function pointingOf(pointerType: string): Pointing {
  return pointerType === 'touch' ? 'finger' : 'tip';
}

export function tipPoint(rect: Rect, view: Viewport, clientX: number, clientY: number): Point {
  const unit = unitOf(view);
  if (unit <= 0 || rect.width <= 0 || rect.height <= 0) return HOME;
  return {
    x: ((clientX - rect.left) * (view.width / rect.width) - view.width / 2) / unit,
    y: ((clientY - rect.top) * (view.height / rect.height) - view.height / 2) / unit,
  };
}

export function fieldBounds(view: Viewport): Point {
  const unit = unitOf(view);
  return unit <= 0 ? HOME : { x: view.width / 2 / unit, y: view.height / 2 / unit };
}

function clampTo(bounds: Point, point: Point): Point {
  return {
    x: Math.max(-bounds.x, Math.min(bounds.x, point.x)),
    y: Math.max(-bounds.y, Math.min(bounds.y, point.y)),
  };
}

function unit01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, value));
}

function smooth(t: number): number {
  return t * t * (3 - 2 * t);
}

export function followPlace(from: Point, target: Point, age: Seconds): Point {
  const t = smooth(unit01(age / FOLLOW_SECONDS));
  return { x: from.x + (target.x - from.x) * t, y: from.y + (target.y - from.y) * t };
}

export function easeSize(ease: SizeEase | null, target: number, now: Seconds): number {
  if (ease === null) return target;
  const t = unit01((now - ease.at) / ease.span);
  return t >= 1 ? target : ease.from + (target - ease.from) * smooth(t);
}

export function createAim(): Aim {
  let place: Point = HOME;
  let glide: Glide | null = null;
  let tipped = false;
  const placeAt = (now: Seconds): Point => (glide === null ? place : followPlace(glide.from, place, now - glide.at));
  const tip = (at: Point, bounds: Point): void => {
    glide = null;
    place = clampTo(bounds, at);
    tipped = true;
  };
  return {
    get place() {
      return place;
    },
    placeAt,
    down(id, pointing, at, bounds, now) {
      if (pointing === 'tip') {
        tip(at, bounds);
        return;
      }
      if (glide !== null) return;
      glide = { id, from: place, at: now };
      place = clampTo(bounds, at);
      tipped = false;
    },
    move(id, pointing, at, bounds) {
      if (pointing === 'tip') tip(at, bounds);
      else if (glide !== null && glide.id === id) place = clampTo(bounds, at);
    },
    up(id, now) {
      if (glide === null || glide.id !== id) return;
      place = placeAt(now);
      glide = null;
    },
    reset() {
      glide = null;
      if (!tipped) place = HOME;
    },
  };
}

export function puckRadius(note: Note | null, beat: Beat): number {
  return note === null ? IDLE_RADIUS : Math.max(IDLE_RADIUS, sizeAt(note, beat));
}

export function missBy(place: Point, note: Note): number {
  return Math.hypot(place.x - note.x, place.y - note.y);
}
